import { useState, useRef, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Building2, User, Mail, MapPin, HardHat, Ruler, Calendar, Sparkles, Loader2,
  CheckCircle2, Clock, Trophy, XCircle, FileText, Check, Plus, Trash2, Phone, ChevronDown, ChevronUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-600', icon: Clock },
  active: { label: 'Active', color: 'bg-blue-100 text-blue-700', icon: FileText },
  analyzing: { label: 'Analyzing', color: 'bg-yellow-100 text-yellow-700', icon: Loader2 },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  quoted: { label: 'Quoted', color: 'bg-purple-100 text-purple-700', icon: FileText },
  won: { label: 'Won', color: 'bg-green-100 text-green-700', icon: Trophy },
  lost: { label: 'Lost', color: 'bg-red-100 text-red-700', icon: XCircle },
  expired: { label: 'Expired', color: 'bg-orange-100 text-orange-700', icon: Clock },
  error: { label: 'Error', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const STATUS_TRANSITIONS = {
  pending: ['active'],
  active: ['quoted', 'lost'],
  analyzing: [],
  completed: ['active', 'quoted'],
  quoted: ['won', 'lost', 'active'],
  won: [],
  lost: ['active'],
  expired: ['active'],
  error: ['active', 'pending']
};

function InlineField({ value, fieldKey, type, placeholder, onSave, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = useCallback(async () => {
    if (draft === (value || '')) { setEditing(false); return; }
    setSaving(true);
    await onSave(fieldKey, draft);
    setSaving(false);
    setEditing(false);
  }, [draft, value, fieldKey, onSave]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
  };

  if (editing) {
    return (
      <div className="relative">
        <input
          ref={inputRef}
          type={type || 'text'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          placeholder={type === 'date' ? 'YYYY-MM-DD' : placeholder}
          className={`w-full bg-white border border-[#1D4053]/30 rounded px-1.5 py-0.5 text-xs text-[#0D1830] outline-none focus:border-[#1D4053] focus:ring-1 focus:ring-[#1D4053]/20 ${className}`}
          data-testid={`input-${fieldKey}`}
        />
        {saving && <Loader2 className="w-3 h-3 animate-spin absolute right-1.5 top-1/2 -translate-y-1/2 text-[#72A4BF]" />}
      </div>
    );
  }

  const displayValue = value
    ? (type === 'date' ? new Date(value + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : value)
    : null;

  return (
    <p
      className={`text-xs text-[#0D1830] truncate cursor-pointer rounded px-1 -mx-1 hover:bg-[#72A4BF]/10 transition-colors ${className}`}
      onDoubleClick={() => setEditing(true)}
      title={value ? `${value} (double-click to edit)` : 'Double-click to edit'}
      data-testid={`field-${fieldKey}`}
    >
      {displayValue || <span className="text-[#72A4BF]/40 italic">click to add</span>}
    </p>
  );
}

function InlineName({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);

  const commit = useCallback(async () => {
    if (!draft.trim() || draft === value) { setDraft(value || ''); setEditing(false); return; }
    setSaving(true);
    await onSave('name', draft.trim());
    setSaving(false);
    setEditing(false);
  }, [draft, value, onSave]);

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); } }}
          className="text-sm font-semibold text-[#0D1830] bg-white border border-[#1D4053]/30 rounded px-2 py-0.5 outline-none focus:border-[#1D4053] focus:ring-1 focus:ring-[#1D4053]/20 flex-1"
          data-testid="input-name"
        />
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#72A4BF]" />}
      </div>
    );
  }

  return (
    <h3
      className="text-sm font-semibold text-[#0D1830] cursor-pointer rounded px-1 -mx-1 hover:bg-[#72A4BF]/10 transition-colors"
      onDoubleClick={() => setEditing(true)}
      title="Double-click to edit"
      data-testid="field-name"
    >
      {value || 'Untitled Project'}
    </h3>
  );
}

function QuoteRecipients({ projectId }) {
  const [recipients, setRecipients] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ company_name: '', contact_name: '', company_address: '', phone: '', email: '', notes: '' });

  const fetchRecipients = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/recipients`, { credentials: 'include' });
      if (res.ok) setRecipients(await res.json());
    } catch (e) {}
  }, [projectId]);

  useEffect(() => { fetchRecipients(); }, [fetchRecipients]);

  const addRecipient = async () => {
    if (!form.company_name.trim() && !form.contact_name.trim()) return;
    try {
      const res = await fetch(`${API}/projects/${projectId}/recipients`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setForm({ company_name: '', contact_name: '', company_address: '', phone: '', email: '', notes: '' });
        setAdding(false);
        fetchRecipients();
      }
    } catch (e) {}
  };

  const deleteRecipient = async (recipientId) => {
    try {
      await fetch(`${API}/projects/${projectId}/recipients/${recipientId}`, {
        method: 'DELETE', credentials: 'include'
      });
      setRecipients(prev => prev.filter(r => r.recipient_id !== recipientId));
    } catch (e) {}
  };

  return (
    <div className="mt-4" data-testid="quote-recipients-section">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs font-semibold text-[#0D1830] hover:text-[#1D4053] transition-colors mb-2"
        data-testid="recipients-toggle"
      >
        <User className="w-3.5 h-3.5" />
        Quote Recipients ({recipients.length})
        {expanded ? <ChevronUp className="w-3 h-3 text-[#72A4BF]" /> : <ChevronDown className="w-3 h-3 text-[#72A4BF]" />}
      </button>

      {expanded && (
        <div className="space-y-2">
          {recipients.map((r, i) => (
            <div key={r.recipient_id} className="flex items-start gap-2 p-2.5 rounded-lg bg-[#F7FAFB] border border-[#E2E8ED] group" data-testid={`recipient-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold text-[#0D1830]">{r.company_name || r.contact_name || 'Unnamed'}</p>
                  {r.notes && <span className="text-[10px] text-[#72A4BF] italic">{r.notes}</span>}
                </div>
                {r.contact_name && r.company_name && <p className="text-[11px] text-[#446E87]">{r.contact_name}</p>}
                <div className="flex flex-wrap gap-3 mt-1">
                  {r.email && <span className="text-[10px] text-[#72A4BF] flex items-center gap-1"><Mail className="w-2.5 h-2.5" />{r.email}</span>}
                  {r.phone && <span className="text-[10px] text-[#72A4BF] flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{r.phone}</span>}
                  {r.company_address && <span className="text-[10px] text-[#72A4BF] flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{r.company_address}</span>}
                </div>
              </div>
              <button
                onClick={() => deleteRecipient(r.recipient_id)}
                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-[#72A4BF] hover:text-red-500 transition-all"
                data-testid={`delete-recipient-${i}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}

          {adding ? (
            <div className="p-3 rounded-lg border border-[#1D4053]/20 bg-white space-y-2" data-testid="add-recipient-form">
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Company name" value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} className="h-7 text-xs" data-testid="recipient-company-name" />
                <Input placeholder="Contact name" value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} className="h-7 text-xs" data-testid="recipient-contact-name" />
                <Input placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="h-7 text-xs" data-testid="recipient-email" />
                <Input placeholder="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="h-7 text-xs" data-testid="recipient-phone" />
                <Input placeholder="Address" value={form.company_address} onChange={e => setForm(f => ({ ...f, company_address: e.target.value }))} className="h-7 text-xs col-span-2" data-testid="recipient-address" />
                <Input placeholder="Notes (e.g. Target Contractor)" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="h-7 text-xs col-span-2" data-testid="recipient-notes" />
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={addRecipient} className="h-7 text-xs bg-[#0D1830] hover:bg-[#1D4053] rounded-lg" data-testid="save-recipient-btn">
                  <Check className="w-3 h-3 mr-1" />Save
                </Button>
                <Button size="sm" variant="outline" onClick={() => setAdding(false)} className="h-7 text-xs rounded-lg">Cancel</Button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="flex items-center gap-1.5 text-xs font-medium text-[#446E87] hover:text-[#0D1830] transition-colors px-2 py-1.5 rounded-lg hover:bg-[#F7FAFB]"
              data-testid="add-recipient-btn"
            >
              <Plus className="w-3 h-3" />Add Recipient
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProjectInfoPanel({ project, onUpdate }) {
  const [extracting, setExtracting] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [savedField, setSavedField] = useState(null);

  const saveField = useCallback(async (key, value) => {
    try {
      const res = await fetch(`${API}/projects/${project.project_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [key]: value })
      });
      if (res.ok) {
        setSavedField(key);
        setTimeout(() => setSavedField(null), 1200);
        onUpdate?.();
      }
    } catch (e) {}
  }, [project?.project_id, onUpdate]);

  const extractInfo = async () => {
    setExtracting(true);
    try {
      const res = await fetch(`${API}/projects/${project.project_id}/extract-info`, {
        method: 'POST', credentials: 'include'
      });
      if (res.ok) onUpdate?.();
    } catch (e) {}
    finally { setExtracting(false); }
  };

  const changeStatus = async (newStatus) => {
    try {
      const res = await fetch(`${API}/projects/${project.project_id}/status/${newStatus}`, {
        method: 'POST', credentials: 'include'
      });
      if (res.ok) { setShowStatusDialog(false); onUpdate?.(); }
    } catch (e) {}
  };

  const currentStatus = project?.status || 'pending';
  const statusConfig = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;
  const allowedTransitions = STATUS_TRANSITIONS[currentStatus] || [];

  const generalFields = [
    { key: 'name', label: 'Project Name', icon: Building2, isName: true },
    { key: 'project_type', label: 'Building Type', icon: Building2 },
    { key: 'project_address', label: 'Project Address', icon: MapPin },
    { key: 'architect', label: 'Architect Firm', icon: Ruler },
  ];

  const pipelineFields = [
    { key: 'bid_start_date', label: 'Expected Start Date', icon: Calendar, type: 'date' },
    { key: 'bid_end_date', label: 'Expected End Date', icon: Calendar, type: 'date' },
  ];

  return (
    <Card className="border-[#E2E8ED] shadow-none" data-testid="project-info-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#0D1830]" />
            Project Details
            <span className="text-[9px] font-normal text-[#72A4BF]/60 ml-1">double-click to edit</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowStatusDialog(true)}
              className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
              data-testid="status-badge"
            >
              <Badge className={`${statusConfig.color} border-0 text-[10px] px-2 py-0.5`}>
                <StatusIcon className={`w-3 h-3 mr-1 ${currentStatus === 'analyzing' ? 'animate-spin' : ''}`} />
                {statusConfig.label}
              </Badge>
            </button>
            <Button
              variant="outline" size="sm"
              onClick={extractInfo}
              disabled={extracting || !project?.file_count}
              className="h-7 text-[10px] rounded-full px-3 border-[#1D4053]/30 text-[#0D1830] hover:bg-[#72A4BF]/10 disabled:opacity-40"
              data-testid="extract-info-btn"
              title={!project?.file_count ? 'Upload documents first' : 'Extract project info from documents using AI'}
            >
              {extracting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
              AI Extract
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {/* General Information */}
        <div className="mb-4">
          <p className="text-[10px] font-semibold text-[#72A4BF] uppercase tracking-wider mb-2">General Information</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {generalFields.map((field) => (
              <div key={field.key} className="flex items-start gap-2 p-2 rounded-lg bg-[#F7FAFB] group relative">
                <field.icon className="w-3.5 h-3.5 text-[#72A4BF] mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-[#72A4BF] uppercase tracking-wide">{field.label}</p>
                  {field.isName ? (
                    <InlineName value={project?.name} onSave={saveField} />
                  ) : (
                    <InlineField
                      value={project?.[field.key] || ''}
                      fieldKey={field.key}
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      onSave={saveField}
                    />
                  )}
                </div>
                {savedField === field.key && (
                  <Check className="w-3 h-3 text-emerald-500 absolute top-1.5 right-1.5 animate-in fade-in" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Pipeline Buildup */}
        <div className="mb-0">
          <p className="text-[10px] font-semibold text-[#72A4BF] uppercase tracking-wider mb-2">Pipeline Buildup</p>
          <div className="grid grid-cols-2 gap-2">
            {pipelineFields.map((field) => (
              <div key={field.key} className="flex items-start gap-2 p-2 rounded-lg bg-[#F7FAFB] group relative">
                <field.icon className="w-3.5 h-3.5 text-[#72A4BF] mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-[#72A4BF] uppercase tracking-wide">{field.label}</p>
                  <InlineField
                    value={project?.[field.key] || ''}
                    fieldKey={field.key}
                    type={field.type}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                    onSave={saveField}
                  />
                </div>
                {savedField === field.key && (
                  <Check className="w-3 h-3 text-emerald-500 absolute top-1.5 right-1.5 animate-in fade-in" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Quote Recipients */}
        {project?.project_id && <QuoteRecipients projectId={project.project_id} />}

        {project?.extracted_info?.confidence && (
          <div className="mt-3 flex items-center gap-2 text-[10px] text-[#72A4BF]">
            <Sparkles className="w-3 h-3 text-[#1D4053]" />
            AI extracted with {Math.round(project.extracted_info.confidence * 100)}% confidence
          </div>
        )}
      </CardContent>

      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Change Project Status</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-[#446E87] mb-3">Current: <Badge className={`${statusConfig.color} border-0 ml-1`}>{statusConfig.label}</Badge></p>
            {allowedTransitions.length > 0 ? (
              <div className="space-y-2">
                {allowedTransitions.map((status) => {
                  const sc = STATUS_CONFIG[status];
                  const Icon = sc.icon;
                  return (
                    <button
                      key={status}
                      onClick={() => changeStatus(status)}
                      className="w-full flex items-center gap-2 p-3 rounded-lg border border-[#E2E8ED] hover:border-[#1D4053]/50 hover:bg-[#72A4BF]/10 transition-colors text-left"
                      data-testid={`status-option-${status}`}
                    >
                      <Icon className="w-4 h-4" /><span className="text-sm font-medium">{sc.label}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-[#72A4BF]">No transitions available.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
